import { CompanyZone } from '_lib/api/apiTypes.shim';
import { AlertDialogProps } from '_lib/hooks/AlertDialogHook';
import { ConfirmationDialogProps } from '_lib/hooks/ConfirmationDialogHook';

import { AsyncDialog } from '_lib/hooks/useAsyncDialog';

import { CompanyContext } from '../callPop/common/CompanyPane/Company.slice';

export const getPhoneCallZipCodeCheckDialogVariant = (
  zipOrPostal: string | null | undefined,
  selectedZone: CompanyZone | null,
  companyZipCodesServiced: string[] | undefined
) => {
  const zipOrPostalCode = zipOrPostal && zipOrPostal.substring(0, 5);
  const zipCodesServiced = companyZipCodesServiced || [];
  if (zipOrPostalCode && zipCodesServiced.length && selectedZone?.id) {
    const isServicedZipCode = zipCodesServiced.includes(zipOrPostalCode);
    // if zip code is not in company, show confirmation dialog
    if (!isServicedZipCode) return 'confirmation' as const;
    // if zip code is in company, but wrong zone, do not allow override - force agent to pick correct zone
    if (isServicedZipCode && !selectedZone.zipCodes.includes(zipOrPostalCode)) return 'warning' as const;
  }
  return false;
};

export const zipZoneConfirmationDialogConfig = {
  titleText: 'Location zip code does not match any zone.',
  contentText: 'Do you still want to complete the call?'
} as const satisfies Partial<ConfirmationDialogProps>;

export const zipZoneAlertDialogConfig = {
  variant: 'error',
  titleText: 'Invalid Zone',
  content: 'Location zip code matches different zone. Verify zip code and select correct zone.'
} as const satisfies Partial<AlertDialogProps>;

/**
 * For Zoned Companies, we have one 'black-box' final check:
 *
 * if location has a ZipCode, company zone is selected for call and zip codes service list exists:
 *  - then if location zip is NOT in the serviced list at all, warn call agent, but allow them to fix/proceed
 *  - if zip is in serviced zip codes, but NOT IN the zone (selected for the phone call) zip code list, HARD
 *    stop the agent and present the error to them
 */
export const zipZoneDialogCheck = async ({
  companyContext,
  selectedZone,
  confirmationCallback,
  confirmationDialog,
  alertDialog,
  zipOrPostal
}: {
  companyContext: CompanyContext;
  selectedZone: CompanyZone | null;
  confirmationCallback: () => Promise<void> | void;
  confirmationDialog: AsyncDialog<unknown, ConfirmationDialogProps>;
  alertDialog: AsyncDialog<unknown, AlertDialogProps>;
  zipOrPostal?: string | null | undefined;
}) => {
  const zipCheckDialogVariant = getPhoneCallZipCodeCheckDialogVariant(
    zipOrPostal,
    selectedZone,
    companyContext.company.callCenter.zipCodesServiced
  );

  if (!zipCheckDialogVariant) {
    await confirmationCallback();
  } else if (zipCheckDialogVariant === 'confirmation') {
    const promise = await confirmationDialog.show(zipZoneConfirmationDialogConfig);
    if (promise?.action === 'CONFIRM') await confirmationCallback();
  } else {
    await alertDialog.show(zipZoneAlertDialogConfig);
  }
};
